export type ListLayoutEntityType =
  | 'CASE'
  | 'INCIDENT'
  | 'PROBLEM'
  | 'CHANGE'
  | 'ALERT'
  | 'SERVICE_REQUEST'
  | 'TIME_ENTRY';

export interface ColumnConfig {
  key: string;
  label: string;
  visible: boolean;
  order: number;
}

export interface ColumnDefinition {
  key: string;
  label: string;
  defaultVisible: boolean;
  defaultOrder: number;
}

// Default column configurations for each entity type
// These mirror the backend defaults and are used for fallback
export const DEFAULT_COLUMNS: Record<ListLayoutEntityType, ColumnDefinition[]> = {
  CASE: [
    { key: 'number', label: 'Number', defaultVisible: true, defaultOrder: 0 },
    { key: 'shortDescription', label: 'Short Description', defaultVisible: true, defaultOrder: 1 },
    { key: 'contact', label: 'Contact', defaultVisible: true, defaultOrder: 2 },
    { key: 'customer', label: 'Customer', defaultVisible: true, defaultOrder: 3 },
    { key: 'engagement', label: 'Engagement', defaultVisible: false, defaultOrder: 4 },
    { key: 'channel', label: 'Channel', defaultVisible: true, defaultOrder: 5 },
    { key: 'state', label: 'State', defaultVisible: true, defaultOrder: 6 },
    { key: 'priority', label: 'Priority', defaultVisible: true, defaultOrder: 7 },
    { key: 'assignedTo', label: 'Assigned To', defaultVisible: true, defaultOrder: 8 },
    { key: 'assignmentGroup', label: 'Assignment Group', defaultVisible: false, defaultOrder: 9 },
    { key: 'impact', label: 'Impact', defaultVisible: false, defaultOrder: 10 },
    { key: 'urgency', label: 'Urgency', defaultVisible: false, defaultOrder: 11 },
    { key: 'category', label: 'Category', defaultVisible: false, defaultOrder: 12 },
    { key: 'resource', label: 'Resource', defaultVisible: false, defaultOrder: 13 },
    { key: 'slaPlan', label: 'Service Plan', defaultVisible: false, defaultOrder: 14 },
    { key: 'slaBreached', label: 'SLA Breached', defaultVisible: false, defaultOrder: 15 },
    { key: 'responseBreachReason', label: 'Response SLA Breach Reason', defaultVisible: false, defaultOrder: 16 },
    { key: 'ongoingBreachReason', label: 'Ongoing SLA Breach Reason', defaultVisible: false, defaultOrder: 17 },
    { key: 'resolutionBreachReason', label: 'Resolution SLA Breach Reason', defaultVisible: false, defaultOrder: 18 },
    { key: 'onHoldReason', label: 'On Hold Reason', defaultVisible: false, defaultOrder: 19 },
    { key: 'solution', label: 'Solution', defaultVisible: false, defaultOrder: 20 },
    { key: 'closedAt', label: 'Closed At', defaultVisible: false, defaultOrder: 21 },
    { key: 'createdBy', label: 'Created By', defaultVisible: true, defaultOrder: 22 },
    { key: 'createdAt', label: 'Created', defaultVisible: true, defaultOrder: 23 },
    { key: 'updatedAt', label: 'Updated', defaultVisible: false, defaultOrder: 24 },
  ],
  INCIDENT: [
    { key: 'number', label: 'Number', defaultVisible: true, defaultOrder: 0 },
    { key: 'shortDescription', label: 'Short Description', defaultVisible: true, defaultOrder: 1 },
    { key: 'reportedBy', label: 'Reported By', defaultVisible: true, defaultOrder: 2 },
    { key: 'customer', label: 'Customer', defaultVisible: true, defaultOrder: 3 },
    { key: 'engagement', label: 'Engagement', defaultVisible: true, defaultOrder: 4 },
    { key: 'priority', label: 'Priority', defaultVisible: true, defaultOrder: 5 },
    { key: 'state', label: 'State', defaultVisible: true, defaultOrder: 6 },
    { key: 'assignedTo', label: 'Assigned To', defaultVisible: true, defaultOrder: 7 },
    { key: 'assignmentGroup', label: 'Assignment Group', defaultVisible: false, defaultOrder: 8 },
    { key: 'impact', label: 'Impact', defaultVisible: false, defaultOrder: 9 },
    { key: 'urgency', label: 'Urgency', defaultVisible: false, defaultOrder: 10 },
    { key: 'category', label: 'Category', defaultVisible: false, defaultOrder: 11 },
    { key: 'slaPlan', label: 'Service Plan', defaultVisible: false, defaultOrder: 12 },
    { key: 'slaBreached', label: 'SLA Breached', defaultVisible: false, defaultOrder: 13 },
    { key: 'responseBreachReason', label: 'Response SLA Breach Reason', defaultVisible: false, defaultOrder: 14 },
    { key: 'ongoingBreachReason', label: 'Ongoing SLA Breach Reason', defaultVisible: false, defaultOrder: 15 },
    { key: 'resolutionBreachReason', label: 'Resolution SLA Breach Reason', defaultVisible: false, defaultOrder: 16 },
    { key: 'resolvedAt', label: 'Resolved At', defaultVisible: false, defaultOrder: 17 },
    { key: 'resolutionNotes', label: 'Resolution Notes', defaultVisible: false, defaultOrder: 18 },
    { key: 'closedAt', label: 'Closed At', defaultVisible: false, defaultOrder: 19 },
    { key: 'closureNotes', label: 'Closure Notes', defaultVisible: false, defaultOrder: 20 },
    { key: 'createdBy', label: 'Created By', defaultVisible: true, defaultOrder: 21 },
    { key: 'createdAt', label: 'Created', defaultVisible: true, defaultOrder: 22 },
  ],
  PROBLEM: [
    { key: 'number', label: 'Number', defaultVisible: true, defaultOrder: 0 },
    { key: 'shortDescription', label: 'Short Description', defaultVisible: true, defaultOrder: 1 },
    { key: 'customer', label: 'Customer', defaultVisible: true, defaultOrder: 2 },
    { key: 'engagement', label: 'Engagement', defaultVisible: true, defaultOrder: 3 },
    { key: 'priority', label: 'Priority', defaultVisible: true, defaultOrder: 4 },
    { key: 'state', label: 'State', defaultVisible: true, defaultOrder: 5 },
    { key: 'assignedTo', label: 'Assigned To', defaultVisible: true, defaultOrder: 6 },
    { key: 'assignmentGroup', label: 'Assignment Group', defaultVisible: true, defaultOrder: 7 },
    { key: 'impact', label: 'Impact', defaultVisible: false, defaultOrder: 8 },
    { key: 'urgency', label: 'Urgency', defaultVisible: false, defaultOrder: 9 },
    { key: 'category', label: 'Category', defaultVisible: false, defaultOrder: 10 },
    { key: 'problemStatement', label: 'Problem Statement', defaultVisible: false, defaultOrder: 11 },
    { key: 'rootCause', label: 'Root Cause', defaultVisible: false, defaultOrder: 12 },
    { key: 'relatedIncidents', label: 'Related Incidents', defaultVisible: false, defaultOrder: 13 },
    { key: 'reportedIssue', label: 'Reported Issue', defaultVisible: false, defaultOrder: 14 },
    { key: 'actualServiceImpact', label: 'Actual Service Impact', defaultVisible: false, defaultOrder: 15 },
    { key: 'briefAnalysis', label: 'Brief Analysis', defaultVisible: false, defaultOrder: 16 },
    { key: 'detailedAnalysis', label: 'Detailed Analysis', defaultVisible: false, defaultOrder: 17 },
    { key: 'correctiveActions', label: 'Corrective Actions', defaultVisible: false, defaultOrder: 18 },
    { key: 'chronologyOfEvents', label: 'Chronology of Events', defaultVisible: false, defaultOrder: 19 },
    { key: 'slaPlan', label: 'Service Plan', defaultVisible: false, defaultOrder: 20 },
    { key: 'slaBreached', label: 'SLA Breached', defaultVisible: false, defaultOrder: 21 },
    { key: 'resolvedAt', label: 'Resolved At', defaultVisible: false, defaultOrder: 22 },
    { key: 'resolution', label: 'Resolution', defaultVisible: false, defaultOrder: 23 },
    { key: 'closedAt', label: 'Closed At', defaultVisible: false, defaultOrder: 24 },
    { key: 'closureNotes', label: 'Closure Notes', defaultVisible: false, defaultOrder: 25 },
    { key: 'createdBy', label: 'Created By', defaultVisible: true, defaultOrder: 26 },
    { key: 'createdAt', label: 'Created', defaultVisible: true, defaultOrder: 27 },
  ],
  CHANGE: [
    { key: 'number', label: 'Number', defaultVisible: true, defaultOrder: 0 },
    { key: 'shortDescription', label: 'Short Description', defaultVisible: true, defaultOrder: 1 },
    { key: 'customer', label: 'Customer', defaultVisible: true, defaultOrder: 2 },
    { key: 'engagement', label: 'Engagement', defaultVisible: true, defaultOrder: 3 },
    { key: 'priority', label: 'Priority', defaultVisible: true, defaultOrder: 4 },
    { key: 'state', label: 'State', defaultVisible: true, defaultOrder: 5 },
    { key: 'assignedTo', label: 'Assigned To', defaultVisible: true, defaultOrder: 6 },
    { key: 'assignmentGroup', label: 'Assignment Group', defaultVisible: false, defaultOrder: 7 },
    { key: 'impact', label: 'Impact', defaultVisible: false, defaultOrder: 8 },
    { key: 'urgency', label: 'Urgency', defaultVisible: false, defaultOrder: 9 },
    { key: 'category', label: 'Category', defaultVisible: false, defaultOrder: 10 },
    { key: 'changeType', label: 'Change Type', defaultVisible: false, defaultOrder: 11 },
    { key: 'riskLevel', label: 'Risk Level', defaultVisible: false, defaultOrder: 12 },
    { key: 'plannedStartDate', label: 'Planned Start', defaultVisible: false, defaultOrder: 13 },
    { key: 'plannedEndDate', label: 'Planned End', defaultVisible: false, defaultOrder: 14 },
    { key: 'actualStartDate', label: 'Actual Start', defaultVisible: false, defaultOrder: 15 },
    { key: 'actualEndDate', label: 'Actual End', defaultVisible: false, defaultOrder: 16 },
    { key: 'justification', label: 'Justification', defaultVisible: false, defaultOrder: 17 },
    { key: 'riskImpactAnalysis', label: 'Risk Impact Analysis', defaultVisible: false, defaultOrder: 18 },
    { key: 'implementationPlan', label: 'Implementation Plan', defaultVisible: false, defaultOrder: 19 },
    { key: 'backoutPlan', label: 'Backout Plan', defaultVisible: false, defaultOrder: 20 },
    { key: 'testPlan', label: 'Test Plan', defaultVisible: false, defaultOrder: 21 },
    { key: 'implementationResults', label: 'Implementation Results', defaultVisible: false, defaultOrder: 22 },
    { key: 'reviewResults', label: 'Review Results', defaultVisible: false, defaultOrder: 23 },
    { key: 'maintenanceWindow', label: 'Maintenance Window', defaultVisible: false, defaultOrder: 24 },
    { key: 'approvalStatus', label: 'CAB Approval Status', defaultVisible: false, defaultOrder: 25 },
    { key: 'customerApprovalStatus', label: 'Customer Approval Status', defaultVisible: false, defaultOrder: 28 },
    { key: 'changeCloseCode', label: 'Close Code', defaultVisible: false, defaultOrder: 29 },
    { key: 'slaPlan', label: 'Service Plan', defaultVisible: false, defaultOrder: 30 },
    { key: 'slaBreached', label: 'SLA Breached', defaultVisible: false, defaultOrder: 31 },
    { key: 'closedAt', label: 'Closed At', defaultVisible: false, defaultOrder: 32 },
    { key: 'closureNotes', label: 'Closure Notes', defaultVisible: false, defaultOrder: 33 },
    { key: 'createdBy', label: 'Created By', defaultVisible: true, defaultOrder: 34 },
    { key: 'createdAt', label: 'Created', defaultVisible: true, defaultOrder: 35 },
  ],
  ALERT: [
    { key: 'number', label: 'Number', defaultVisible: true, defaultOrder: 0 },
    { key: 'shortDescription', label: 'Short Description', defaultVisible: true, defaultOrder: 1 },
    { key: 'severity', label: 'Severity', defaultVisible: true, defaultOrder: 2 },
    { key: 'state', label: 'State', defaultVisible: true, defaultOrder: 3 },
    { key: 'source', label: 'Source', defaultVisible: true, defaultOrder: 4 },
    { key: 'customer', label: 'Customer', defaultVisible: true, defaultOrder: 5 },
    { key: 'engagement', label: 'Engagement', defaultVisible: true, defaultOrder: 6 },
    { key: 'resource', label: 'Resource', defaultVisible: true, defaultOrder: 7 },
    { key: 'category', label: 'Category', defaultVisible: false, defaultOrder: 8 },
    { key: 'alertType', label: 'Alert Type', defaultVisible: false, defaultOrder: 9 },
    { key: 'acknowledged', label: 'Acknowledged', defaultVisible: false, defaultOrder: 10 },
    { key: 'acknowledgedBy', label: 'Acknowledged By', defaultVisible: false, defaultOrder: 11 },
    { key: 'acknowledgedAt', label: 'Acknowledged At', defaultVisible: false, defaultOrder: 12 },
    { key: 'count', label: 'Alert Count', defaultVisible: false, defaultOrder: 13 },
    { key: 'firstSeen', label: 'First Seen', defaultVisible: false, defaultOrder: 14 },
    { key: 'lastSeen', label: 'Last Seen', defaultVisible: false, defaultOrder: 15 },
    { key: 'createdAt', label: 'Created', defaultVisible: true, defaultOrder: 16 },
  ],
  SERVICE_REQUEST: [
    { key: 'number', label: 'Number', defaultVisible: true, defaultOrder: 0 },
    { key: 'shortDescription', label: 'Short Description', defaultVisible: true, defaultOrder: 1 },
    { key: 'contact', label: 'Contact', defaultVisible: true, defaultOrder: 2 },
    { key: 'customer', label: 'Customer', defaultVisible: true, defaultOrder: 3 },
    { key: 'engagement', label: 'Engagement', defaultVisible: true, defaultOrder: 4 },
    { key: 'priority', label: 'Priority', defaultVisible: true, defaultOrder: 5 },
    { key: 'state', label: 'State', defaultVisible: true, defaultOrder: 6 },
    { key: 'assignedTo', label: 'Assigned To', defaultVisible: true, defaultOrder: 7 },
    { key: 'assignmentGroup', label: 'Assignment Group', defaultVisible: false, defaultOrder: 8 },
    { key: 'impact', label: 'Impact', defaultVisible: false, defaultOrder: 9 },
    { key: 'urgency', label: 'Urgency', defaultVisible: false, defaultOrder: 10 },
    { key: 'category', label: 'Category', defaultVisible: false, defaultOrder: 11 },
    { key: 'channel', label: 'Channel', defaultVisible: false, defaultOrder: 12 },
    { key: 'slaPlan', label: 'Service Plan', defaultVisible: false, defaultOrder: 13 },
    { key: 'slaBreached', label: 'SLA Breached', defaultVisible: false, defaultOrder: 14 },
    { key: 'responseBreachReason', label: 'Response SLA Breach Reason', defaultVisible: false, defaultOrder: 15 },
    { key: 'ongoingBreachReason', label: 'Ongoing SLA Breach Reason', defaultVisible: false, defaultOrder: 16 },
    { key: 'resolutionBreachReason', label: 'Resolution SLA Breach Reason', defaultVisible: false, defaultOrder: 17 },
    { key: 'resolvedAt', label: 'Resolved At', defaultVisible: false, defaultOrder: 18 },
    { key: 'resolutionNotes', label: 'Resolution Notes', defaultVisible: false, defaultOrder: 19 },
    { key: 'closedAt', label: 'Closed At', defaultVisible: false, defaultOrder: 20 },
    { key: 'closureNotes', label: 'Closure Notes', defaultVisible: false, defaultOrder: 21 },
    { key: 'createdBy', label: 'Created By', defaultVisible: true, defaultOrder: 22 },
    { key: 'createdAt', label: 'Created', defaultVisible: true, defaultOrder: 23 },
  ],
  TIME_ENTRY: [
    { key: 'number', label: 'Entry #', defaultVisible: true, defaultOrder: 0 },
    { key: 'workedDate', label: 'Worked Date', defaultVisible: true, defaultOrder: 1 },
    { key: 'user', label: 'User', defaultVisible: true, defaultOrder: 2 },
    { key: 'customer', label: 'Customer', defaultVisible: true, defaultOrder: 3 },
    { key: 'engagement', label: 'Engagement', defaultVisible: true, defaultOrder: 4 },
    { key: 'group', label: 'Group', defaultVisible: true, defaultOrder: 5 },
    { key: 'type', label: 'Type', defaultVisible: true, defaultOrder: 6 },
    { key: 'task', label: 'Task', defaultVisible: false, defaultOrder: 7 },
    { key: 'hours', label: 'Hours', defaultVisible: true, defaultOrder: 8 },
    { key: 'description', label: 'Description', defaultVisible: true, defaultOrder: 9 },
    { key: 'status', label: 'Status', defaultVisible: true, defaultOrder: 10 },
    { key: 'createdAt', label: 'Created', defaultVisible: false, defaultOrder: 11 },
  ],
};

// Entity labels for display
export const ENTITY_LABELS: Record<ListLayoutEntityType, string> = {
  CASE: 'Cases',
  INCIDENT: 'Incidents',
  PROBLEM: 'Problems',
  CHANGE: 'Changes',
  ALERT: 'Alerts',
  SERVICE_REQUEST: 'Service Requests',
  TIME_ENTRY: 'Time Entries',
};

// Helper function to get default columns as ColumnConfig array
export function getDefaultColumns(entityType: ListLayoutEntityType): ColumnConfig[] {
  return DEFAULT_COLUMNS[entityType].map(col => ({
    key: col.key,
    label: col.label,
    visible: col.defaultVisible,
    order: col.defaultOrder,
  }));
}
